import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text, Line, Arrow, Group, Circle } from 'react-konva';
import { useTranslation } from 'react-i18next';
import containerImage from '../../../images/container.png'; // Assicurati percorso corretto
import tireImageSrc from '../../../images/pneumatico.png'; // Import tire image
import texture1Image from '../../../images/asfalto.png';
import texture2Image from '../../../images/basecourse.png';
import texture4Image from '../../../images/subgrade.png';
import { MAX_CANVAS_HEIGHT } from '../../../Pavements/components/Results/canvasConstants';

// Interfacce per le Props (come prima)
export interface ILayerData {
    name: string;
    thickness: number;
    color: string;
}

export interface IGeogridInfo {
    positionFromBottomOfBase: number; // Dal fondo del Base Course
    label?: string;
    color?: string;
}

interface Props {
    surfaceLayer: ILayerData;
    baseCourse: ILayerData;
    subgrade: ILayerData; // Spessore = altezza visibile
    leftDimensionValue?: number;
    rightDimensionValue?: number;
    savingPercentage?: number;
    geogrid?: IGeogridInfo;
    subbaseGeogrid?: IGeogridInfo;
    showGeotextileLabel?: boolean;
    unitLabel?: string;
    showLeftDimension?: boolean;
    showRightDimension?: boolean;
    showSavingBox?: boolean;
    showGeogrid?: boolean;
    showTire?: boolean; // This prop might become redundant or be re-evaluated
    showLayers?: boolean; // New prop
    leftDimensionStrokeWidth?: number; // New prop
    horizontalSpacing?: number; // Aggiungi '?' se sono opzionali
    verticalSpacing?: number; // Aggiungi '?' se sono opzionali

    customStageHeight?: number;
    // Layer stack thickness of the tallest panel drawn side by side: every panel scales on it, so their thicknesses stay comparable
    scaleReferenceThickness?: number;
    onStageHeightCalculated?: (height: number) => void;
    onSubgradeYCalculated?: (y: number) => void;
    alignFromBottom?: boolean;
    targetSubgradeY?: number;
    hideLayerTitles?: boolean;
    hideSurfaceLabel?: boolean; // <-- nuova prop per nascondere solo il titolo 'surface'
    projectType?: 'container_yard' | 'parking_deck' | string; // Added projectType
}

// The surface layer is not drawn (height 0), so only base course and subgrade take vertical space
export const getDrawnLayerStackThickness = (baseCourse: ILayerData, subgrade: ILayerData): number => baseCourse.thickness + subgrade.thickness;

// --- Componente ---
const TireAndLayers: React.FC<Props> = ({
    surfaceLayer,
    baseCourse,
    subgrade,
    leftDimensionValue,
    rightDimensionValue,
    savingPercentage,
    geogrid,
    subbaseGeogrid,
    showGeotextileLabel,
    unitLabel = 'm',
    showLeftDimension = false,
    showRightDimension = false,
    showSavingBox = false,
    showGeogrid = false,
    showTire = true, // Keep for now, might be controlled by projectType implicitly
    showLayers = true,
    leftDimensionStrokeWidth,
    customStageHeight,
    scaleReferenceThickness,
    onStageHeightCalculated,
    onSubgradeYCalculated,
    alignFromBottom = false,
    targetSubgradeY,
    hideLayerTitles = false,
    hideSurfaceLabel = false, // <-- default false
    projectType = 'container_yard', // Default to container_yard
}) => {
    const { t } = useTranslation();

    // Padding modificabile per il label Geogrid
    const geogridLabelPadding = 20;
    // Ref e stato per larghezza testo Geogrid
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const geogridTextRef = useRef<any>(null);
    const [geogridTextWidth, setGeogridTextWidth] = useState(0);
    const [geogridTextHeight, setGeogridTextHeight] = useState(0); // Altezza del testo geogrid
    // Ref e stato per subbaseGeogrid
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subbaseGeogridTextRef = useRef<any>(null);
    const [subbaseGeogridTextWidth, setSubbaseGeogridTextWidth] = useState(0);
    const [subbaseGeogridTextHeight, setSubbaseGeogridTextHeight] = useState(0);
    // Ref e stato per geotextile
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const geotextileTextRef = useRef<any>(null);
    const [geotextileTextWidth, setGeotextileTextWidth] = useState(0);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [geotextileTextHeight, setGeotextileTextHeight] = useState(0);
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [textureImg, setTextureImg] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);
    // Refs per i titoli
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const surfaceTextRef = useRef<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseTextRef = useRef<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subgradeTextRef = useRef<any>(null);
    // Ref per il gruppo destro (dimensioni)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rightDimGroupRef = useRef<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rightDimTextRef = useRef<any>(null);
    // Stato solo per larghezza e posizione X del gruppo destro
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [calcoloRightDim, setCalcoloRightDim] = useState(0);
    const [calcoloRightX, setCalcoloRightX] = useState(0);
    // --- Calcoli Dimensioni e Posizioni (3 STRATI) ---
    // const tireHeight = containerW * 1.1; // Non più necessario per il layout Y
    // const tireImageVisualHeight = tireHeight * 2; // Non più necessario per il layout Y, causa dello spazio

    const stageTopMargin = 10;
    const stageBottomMargin = 10;

    const leftDimTextWidth = 35;
    // const leftOffset = leftDimTextWidth + leftDimArrowAreaWidth; // Se serve, dichiarare leftDimArrowAreaWidth

    const titleAreaWidth = 100;
    const leftDimAreaWidth = 0; // spazio dalla fine delle scritte al quadro dei livelli
    const rectStartX = titleAreaWidth + leftDimAreaWidth;
    const squareWidth = 360;
    const rectEndX = rectStartX + squareWidth;
    const rightDimAreaWidth = 50;
    const savingBoxWidth = 160;
    const savingBoxHeight = 80;
    const savingBoxAreaWidth = savingBoxWidth + 15;
    // Offset X per il saving box (modificabile)
    const offsetXsavingbox = -100;
    // La larghezza dello stage è dinamica
    const [stageWidth, setStageWidth] = useState(rectEndX + rightDimAreaWidth + savingBoxAreaWidth);

    // posizine del label geogrid:
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const X_geogridLabelGroup = rectStartX + squareWidth / 2 - (geogridTextWidth + geogridLabelPadding) / 2;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const Y_geogridLabelGroup = geogridTextHeight + geogridLabelPadding / 2;

    // --- Image selection and dimensions based on projectType ---
    const isParkingDeck = projectType === 'parking_deck';
    const imageSrcToLoad = isParkingDeck ? tireImageSrc : containerImage;

    // Dimensions for Container (container_yard)
    const CONTAINER_IMAGE_HEIGHT = 100;
    const CONTAINER_IMAGE_WIDTH = squareWidth - 150;

    // Dimensions for Tire (parking_deck) - Adjust as needed
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const TIRE_IMAGE_RADIUS = 65; // Example, match Graphic/TireAndLayers.tsx if desired
    // const TIRE_IMAGE_HEIGHT = TIRE_IMAGE_RADIUS * 2;
    // const TIRE_IMAGE_WIDTH = squareWidth - 180; // Example
    const TIRE_RAPPORTO_DIAGONALE = 2.2; // 350 / 150 = 2.333
    const TIRE_IMAGE_HEIGHT = 150;
    const TIRE_IMAGE_WIDTH = TIRE_IMAGE_HEIGHT / TIRE_RAPPORTO_DIAGONALE;

    const currentImageHeight = isParkingDeck ? TIRE_IMAGE_HEIGHT : CONTAINER_IMAGE_HEIGHT;
    const currentImageWidth = isParkingDeck ? TIRE_IMAGE_WIDTH : CONTAINER_IMAGE_WIDTH;
    // End image selection

    // Above MAX_CANVAS_HEIGHT the layers shrink to the space left by the image and margins (Paved pattern):
    // an uncapped stage (e.g. 1500195 px for h = 10000 m) exceeds the browser canvas limit and renders blank
    const baseScale = 150;
    const layerStackThickness = Math.max(getDrawnLayerStackThickness(baseCourse, subgrade), scaleReferenceThickness ?? 0);
    const preliminaryCanvasHeight = stageTopMargin + currentImageHeight + layerStackThickness * baseScale + stageBottomMargin;
    const availableSpaceForLayers = MAX_CANVAS_HEIGHT - stageTopMargin - currentImageHeight - stageBottomMargin;
    const scale = preliminaryCanvasHeight > MAX_CANVAS_HEIGHT ? availableSpaceForLayers / layerStackThickness : baseScale;
    // const surfaceHeightPx = Math.max(surfaceLayer.thickness * scale, 1);
    const surfaceHeightPx = 0;
    const baseCourseHeightPx = Math.max(baseCourse.thickness * scale, 1);
    const subgradeHeightPx = Math.max(subgrade.thickness * scale, 1);
    const totalLayerHeight = surfaceHeightPx + baseCourseHeightPx + subgradeHeightPx;

    // Coordinate Y
    const tireImageY = stageTopMargin; // The Y of the container/tire starts from stageTopMargin

    // MODIFICA: surfaceY ora inizia subito dopo currentImageHeight
    // const surfaceY = tireImageY + tireImageVisualHeight; // Vecchio calcolo che creava lo spazio

    // const surfaceY = tireImageY + containerHeight; // Vecchio calcolo: il surface inizia dove finisce il container
    // const baseY = surfaceY + surfaceHeightPx;
    // const subgradeY = baseY + baseCourseHeightPx;

    //const totalLayerHeight = surfaceHeightPx + baseCourseHeightPx + subgradeHeightPx;

    let surfaceY: number, baseY: number, subgradeY: number, stageHeight: number;

    if (alignFromBottom && targetSubgradeY !== undefined) {
        subgradeY = targetSubgradeY;
        baseY = subgradeY - baseCourseHeightPx;
        surfaceY = baseY - surfaceHeightPx;
        // Stage height calculation needs to consider the image on top if not aligning from bottom
        // For alignFromBottom, the image position is relative to surfaceY, so stageHeight is primarily driven by layers.
        // However, ensure enough space for the image if surfaceY becomes very small.
        const topSpaceForImage = currentImageHeight + stageTopMargin;
        const layersEndY = subgradeY + subgradeHeightPx;
        // If surfaceY is pushed very high by alignFromBottom, ensure stageHeight accommodates the image.
        // This might need adjustment if the image is meant to be outside the calculated layer area.
        // For now, assume surfaceY is the top of the layers, and image sits on it.
        stageHeight = customStageHeight ?? Math.min(Math.max(layersEndY + stageBottomMargin, topSpaceForImage + totalLayerHeight + stageBottomMargin), MAX_CANVAS_HEIGHT);
    } else {
        surfaceY = tireImageY + currentImageHeight; // Use currentImageHeight
        baseY = surfaceY + surfaceHeightPx;
        subgradeY = baseY + baseCourseHeightPx;
        // Math.min absorbs the sub-pixel overshoot of the 1 px minimum layer height
        stageHeight = customStageHeight ?? Math.min(surfaceY + totalLayerHeight + stageBottomMargin, MAX_CANVAS_HEIGHT);
    }

    // Calcola larghezza testo Geogrid
    useEffect(() => {
        if (geogridTextRef.current) {
            setGeogridTextWidth(geogridTextRef.current.getTextWidth?.() || 0);
            setGeogridTextHeight(geogridTextRef.current.getTextHeight?.() || 0); // Calcola anche l'altezza
        }
    }, [geogrid?.label]);
    useEffect(() => {
        if (subbaseGeogridTextRef.current) {
            setSubbaseGeogridTextWidth(subbaseGeogridTextRef.current.getTextWidth?.() || 0);
            setSubbaseGeogridTextHeight(subbaseGeogridTextRef.current.getTextHeight?.() || 0);
        }
    }, []);
    useEffect(() => {
        if (geotextileTextRef.current) {
            setGeotextileTextWidth(geotextileTextRef.current.getTextWidth?.() || 0);
            setGeotextileTextHeight(geotextileTextRef.current.getTextHeight?.() || 0);
        }
    }, []);

    // Calcola solo larghezza e posizione X del gruppo destro
    useEffect(() => {
        const quotaTextWidth = rightDimTextRef.current?.getTextWidth?.() || 0;
        const rightDim = quotaTextWidth;
        setCalcoloRightDim(rightDim);
        const rightX = rightDimTextRef.current?.x() ?? 0;
        setCalcoloRightX(rightX);
        // Calcolo posizione X del saving box
        const savingBoxXReinforced = rightX + offsetXsavingbox;
        // stageWidth: include savingBox solo se visibile e savingPercentage > 0
        if (showSavingBox && typeof savingPercentage === 'number' && savingPercentage > 0) {
            setStageWidth(savingBoxXReinforced + savingBoxWidth + stageRightMargin);
        } else {
            setStageWidth(rightX + rightDim + stageRightMargin);
        }
    }, [rightDimensionValue, unitLabel, offsetXsavingbox, savingBoxWidth]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (onStageHeightCalculated) onStageHeightCalculated(stageHeight);
        if (onSubgradeYCalculated) onSubgradeYCalculated(subgradeY);
    }, [stageHeight, subgradeY, onStageHeightCalculated, onSubgradeYCalculated]);

    useEffect(() => {
        if (onStageHeightCalculated) {
            onStageHeightCalculated(stageHeight);
        }
    }, [stageHeight, onStageHeightCalculated]);

    // === QUOTE (DIMENSIONI) ===
    const dimColor = '#004652';
    const marrone = '#9e480e';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const profiloSaving = 3;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const fillSaving = 'white';
    const dimStrokeWidth = 2; // era 2.5, raddoppiato
    const thickDimLineWidth = 22; // era 16, raddoppiato
    const thickDimLineSpacing = 16; // era 8, raddoppiato
    const effectiveLeftDimStrokeWidth = leftDimensionStrokeWidth ?? dimStrokeWidth;
    const extensionLineOffset = 0; // era 6, raddoppiato
    const extensionLineLength = 30; // era 15, raddoppiato
    const dimensionLineOffsetX = 16; // era 8, raddoppiato
    const textOffsetY = -6; // era -6, raddoppiato
    const dimTextProps = { fontSize: 13, fill: 'black', fontStyle: 'bold' }; // fontSize raddoppiato
    const dimArrowProps = { stroke: marrone, fill: marrone, strokeWidth: 1, pointerLength: 10, pointerWidth: 5 }; // pointerLength/Width raddoppiati
    const leftDimLineX = rectStartX - extensionLineOffset - dimensionLineOffsetX;
    const rightDimLineX = rectEndX + extensionLineOffset + dimensionLineOffsetX;

    const savingLabelOffsetY = -25;
    const stageRightMargin = 10;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const savingBoxX = stageWidth - savingBoxWidth - stageRightMargin;
    const savingBoxY = stageTopMargin + Math.abs(savingLabelOffsetY);

    const geogridColor = geogrid?.color || '#24244f';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const geogridColor2 = 'red';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const geogridStrokeWidth = 1.5;
    const layerStrokeColor = '#444444';
    const layerStrokeWidth = 0.5;

    // const [tireImage, setTireImage] = useState<HTMLImageElement | null>(null); // Renamed to image
    useEffect(() => {
        const imgToLoad = new window.Image();
        imgToLoad.src = imageSrcToLoad; // Use dynamically selected image source
        imgToLoad.onload = () => setImage(imgToLoad);
        imgToLoad.onerror = () => {
            // Intentionally empty - silently ignore image load errors
        };

        const tex1 = new window.Image();
        tex1.src = texture1Image;
        tex1.onload = () => setTextureImg(tex1);
        tex1.onerror = () => {
            // Intentionally empty - silently ignore texture load errors
        };

        const tex2 = new window.Image();
        tex2.src = texture2Image;
        tex2.onload = () => setTexture2Img(tex2);
        tex2.onerror = () => {
            // Intentionally empty - silently ignore texture load errors
        };

        const tex4 = new window.Image();
        tex4.src = texture4Image;
        tex4.onload = () => setTexture4Img(tex4);
        tex4.onerror = () => {
            // Intentionally empty - silently ignore texture load errors
        };
    }, [imageSrcToLoad]); // Add imageSrcToLoad to dependency array

    // const rectStartX = 10;
    // const rectEndX = 390;
    // custom riga con pallini
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const y = 25; // fissa Y come nel tuo esempio
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const canvasWidth = 420;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const canvasHeight = 52;
    const nPoints = 6;
    const cerchioRaggio = 6;
    const circleSpacing = (rectEndX - (rectStartX + 0)) / (nPoints - 1);
    const circleXs = Array.from({ length: nPoints }, (_, i) => rectStartX - 6 + i * circleSpacing);

    return (
        <Stage width={stageWidth} height={stageHeight}>
            <Layer>
                {/* === Titoli Layer (3 strati) === */}
                {showLayers && (
                    <>
                        {/* Retta invisibile / riquadro stage mostrato solo se non si vogliono nascondere tutte le label */}
                        {!hideLayerTitles && <Rect x={0} y={0} width={stageWidth} height={stageHeight} />}

                        {/* Surface title: visibile solo se non si nascondono tutte le label e non è richiesta la rimozione specifica */}
                        {!hideLayerTitles && !hideSurfaceLabel && (
                            <Text
                                ref={surfaceTextRef}
                                text={t(surfaceLayer.name) ?? ''}
                                x={0}
                                y={surfaceY + surfaceHeightPx / 2 + textOffsetY + 3}
                                width={titleAreaWidth - 10}
                                align="right"
                                fontSize={13}
                                fill="black"
                            />
                        )}

                        {/* Base e subgrade: visibili se non si nascondono tutte le label */}
                        {!hideLayerTitles && (
                            <Text
                                ref={baseTextRef}
                                text={t(baseCourse.name) ?? ''}
                                x={0}
                                y={baseY + baseCourseHeightPx / 2 + textOffsetY}
                                width={titleAreaWidth - 10}
                                align="right"
                                fontSize={13}
                                fill="black"
                            />
                        )}
                        {!hideLayerTitles && (
                            <Text
                                ref={subgradeTextRef}
                                text={t(subgrade.name) ?? ''}
                                x={0}
                                y={subgradeY + subgradeHeightPx / 2 + textOffsetY}
                                width={titleAreaWidth - 10}
                                align="right"
                                fontSize={13}
                                fill="black"
                            />
                        )}
                    </>
                )}

                {/* === Rettangoli Layer (3 strati) - Condizionale === */}
                {showLayers && (
                    <>
                        <Rect
                            x={rectStartX}
                            y={surfaceY}
                            width={squareWidth}
                            height={surfaceHeightPx}
                            fillPatternImage={textureImg || undefined}
                            fillPatternRepeat="repeat"
                            fillPatternScale={{ x: 0.2, y: 0.2 }}
                            stroke={layerStrokeColor}
                            strokeWidth={layerStrokeWidth}
                        />
                        <Rect
                            x={rectStartX}
                            y={baseY}
                            width={squareWidth}
                            height={baseCourseHeightPx}
                            fillPatternImage={texture2Img || undefined}
                            fillPatternRepeat="repeat"
                            fillPatternScale={{ x: 0.8, y: 0.8 }}
                            stroke={layerStrokeColor}
                            strokeWidth={layerStrokeWidth}
                            opacity={1}
                        />
                        <Rect
                            x={rectStartX}
                            y={subgradeY}
                            width={squareWidth}
                            height={subgradeHeightPx}
                            fillPatternImage={texture4Img || undefined}
                            fillPatternRepeat="repeat"
                            fillPatternScale={{ x: 0.5, y: 0.5 }}
                            stroke={layerStrokeColor}
                            strokeWidth={layerStrokeWidth}
                        />
                    </>
                )}

                {/* === container/tire (Condizionale) === */}
                {/* L'immagine del container/tire viene disegnata con y calcolata e height=currentImageHeight */}
                {/* La sua base sarà quindi a surfaceY (top of surface layer) */}
                {showLayers && showTire && image && (
                    <KonvaImage
                        image={image}
                        x={rectStartX + squareWidth / 2 - currentImageWidth / 2}
                        y={surfaceY - currentImageHeight} // Positioned on top of the surface layer
                        width={currentImageWidth}
                        height={currentImageHeight}
                    />
                )}

                {/* === Linea Geogriglia (Condizionale) === */}
                {showLayers &&
                    showGeogrid &&
                    geogrid &&
                    typeof geogrid.positionFromBottomOfBase === 'number' &&
                    (() => {
                        const geogridY = baseY + baseCourseHeightPx - geogrid.positionFromBottomOfBase * scale;
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const subgradeY = baseY + baseCourseHeightPx;

                        // Debug logging for geogrid positioning
                        // console.log('[TireAndLayers] Geogrid positioning debug:');
                        // console.log('[TireAndLayers] baseY:', baseY);
                        // console.log('[TireAndLayers] baseCourseHeightPx:', baseCourseHeightPx);
                        // console.log('[TireAndLayers] subgradeY (baseY + baseCourseHeightPx):', subgradeY);
                        // console.log('[TireAndLayers] geogrid.positionFromBottomOfBase:', geogrid.positionFromBottomOfBase);
                        // console.log('[TireAndLayers] scale:', scale);
                        // console.log('[TireAndLayers] geogridY calculated:', geogridY);
                        // console.log('[TireAndLayers] Distance from subgrade (negative = below):', geogridY - subgradeY);

                        return (
                            <Group y={geogridY}>
                                {/* Linea GEOGRID principale con effetto glow */}
                                <Line
                                    points={[rectStartX, 0, rectEndX, 0]}
                                    stroke={geogridColor}
                                    strokeWidth={3}
                                    shadowColor="#FFFFFF"
                                    // shadowBlur={8}
                                    opacity={0.8}
                                    lineCap="round"
                                />
                                {/* Cerchietti */}
                                {circleXs.map((x, idx) => (
                                    <Circle
                                        key={idx}
                                        x={x + 6}
                                        y={0}
                                        radius={cerchioRaggio}
                                        fill="white"
                                        stroke={geogridColor}
                                        strokeWidth={2}
                                        shadowColor="#FFFFFF"
                                        // shadowBlur={8}
                                        opacity={1}
                                    />
                                ))}
                                {/* Geogrid label se presente */}
                                {geogrid.label && (
                                    <Group x={rectStartX + squareWidth / 2 - (geogridTextWidth + geogridLabelPadding) / 2} y={-(geogridTextHeight + geogridLabelPadding / 2)}>
                                        <Rect
                                            width={geogridTextWidth + geogridLabelPadding}
                                            height={geogridTextHeight + geogridLabelPadding / 2}
                                            fill="#FFFFFF"
                                            cornerRadius={6}
                                            x={0}
                                            y={-10}
                                            stroke="#828282"
                                            strokeWidth={1}
                                        />
                                        <Text ref={geogridTextRef} text={geogrid.label} fontSize={12} fontStyle="bold" fill="#000000" x={geogridLabelPadding / 2} y={-4} />
                                    </Group>
                                )}
                                {/* SubbaseGeogrid label (se presente) */}
                                {typeof subbaseGeogrid !== 'undefined' && subbaseGeogrid?.label && (
                                    <Group x={rectStartX + squareWidth / 2 - (subbaseGeogridTextWidth + geogridLabelPadding) / 2} y={-(subbaseGeogridTextHeight + geogridLabelPadding / 2)}>
                                        <Rect
                                            width={subbaseGeogridTextWidth + geogridLabelPadding}
                                            height={subbaseGeogridTextHeight + geogridLabelPadding / 2}
                                            fill="#FFFFFF"
                                            cornerRadius={6}
                                            x={0}
                                            y={-10}
                                            stroke="#828282"
                                            strokeWidth={2}
                                        />
                                        <Text ref={subbaseGeogridTextRef} text={subbaseGeogrid.label} fontSize={14} fontStyle="bold" fill="#000000" x={geogridLabelPadding / 2} y={-5} />
                                    </Group>
                                )}
                                {/* Geotextile label (se presente) */}
                                {showGeotextileLabel && (
                                    <Group x={rectStartX + squareWidth / 2 - (geotextileTextWidth + geogridLabelPadding) / 2} y={-(geotextileTextHeight + geogridLabelPadding / 2)}>
                                        <Rect
                                            width={geotextileTextWidth + geogridLabelPadding}
                                            height={geotextileTextHeight + geogridLabelPadding / 2}
                                            fill="#FFFFFF"
                                            cornerRadius={6}
                                            x={0}
                                            y={-10}
                                            stroke="#828282"
                                            strokeWidth={1}
                                        />
                                        <Text ref={geotextileTextRef} text="Geotextile" fontSize={14} fontStyle="bold" fill="#000000" x={geogridLabelPadding / 2} y={-5} />
                                    </Group>
                                )}
                            </Group>
                        );
                    })()}

                {/* === Linee di Quota (Condizionali) === */}
                {showLeftDimension && typeof leftDimensionValue === 'number' && leftDimensionValue > 0 && (
                    <Group>
                        <Rect x={leftDimLineX + thickDimLineSpacing} y={baseY} width={thickDimLineWidth} height={baseCourseHeightPx} fill={'dimColor'} />
                        <Line points={[rectStartX - extensionLineOffset, baseY, rectStartX - extensionLineOffset - extensionLineLength, baseY]} stroke={dimColor} strokeWidth={2} dash={[3, 2]} />
                        <Line
                            points={[rectStartX - extensionLineOffset, subgradeY, rectStartX - extensionLineOffset - extensionLineLength, subgradeY]}
                            stroke={dimColor}
                            strokeWidth={2}
                            dash={[3, 2]}
                        />
                        <Arrow points={[leftDimLineX, baseY, leftDimLineX, subgradeY]} {...dimArrowProps} strokeWidth={effectiveLeftDimStrokeWidth} pointerAtBeginning={true} />
                        <Text text={`${leftDimensionValue.toFixed(2)} ${unitLabel}`} x={leftDimTextWidth - 5} y={baseY + baseCourseHeightPx / 2 + textOffsetY} {...dimTextProps} rotation={-90} />
                    </Group>
                )}

                {showRightDimension && typeof rightDimensionValue === 'number' && rightDimensionValue > 0 && (
                    <Group ref={rightDimGroupRef}>
                        {/* <Rect x={rightDimLineX - thickDimLineWidth - thickDimLineSpacing} y={baseY} width={thickDimLineWidth} height={baseCourseHeightPx} fill={dimColor} /> */}
                        <Line points={[rectEndX + extensionLineOffset, baseY, rectEndX + extensionLineOffset + extensionLineLength, baseY]} stroke={marrone} strokeWidth={1} dash={[3, 2]} />
                        <Line points={[rectEndX + extensionLineOffset, subgradeY, rectEndX + extensionLineOffset + extensionLineLength, subgradeY]} stroke={marrone} strokeWidth={1} dash={[3, 2]} />
                        <Arrow points={[rightDimLineX, baseY, rightDimLineX, subgradeY]} {...dimArrowProps} pointerAtBeginning={true} />
                        <Text ref={rightDimTextRef} text={`${rightDimensionValue.toFixed(2)} ${unitLabel}`} x={rightDimLineX + 5} y={baseY + baseCourseHeightPx / 2 + textOffsetY} {...dimTextProps} />
                    </Group>
                )}

                {/* === Box Saving Percentage (Condizionale) === */}
                {showLayers && showSavingBox && typeof savingPercentage === 'number' && savingPercentage > 0 && (
                    <Group x={calcoloRightX + offsetXsavingbox} y={savingBoxY + 20} width={savingBoxWidth}>
                        <Rect
                            width={savingBoxWidth}
                            height={savingBoxHeight}
                            // fill={dimColor}
                            fill="white" // Riempimento bianco
                            stroke="#004652" // Bordo blu scuro
                            strokeWidth={3} // Spessore bordo 3
                            cornerRadius={4}
                        />
                        <Text text={t('thickness_saving') ?? 'Saving'} x={0} y={12} width={savingBoxWidth} align="center" fontSize={15} fill={dimColor} fontStyle="italic" />
                        <Text
                            text={`-${savingPercentage.toFixed(0)}%`}
                            width={savingBoxWidth}
                            height={savingBoxHeight}
                            align="center"
                            y={-9}
                            verticalAlign="bottom"
                            fontSize={35}
                            fill="#004652"
                            fontStyle="bold"
                        />
                    </Group>
                )}
            </Layer>
        </Stage>
    );
};
export default TireAndLayers;
